import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Phone, WhatsappLogo, FileText, PencilSimple, Path, Clock, Prohibit } from "@phosphor-icons/react";
import { EVENT_COLORS, formatRange } from "@/lib/calendar";
import { openWhatsApp } from "@/lib/contact";
import api from "@/lib/api";
import { toast } from "sonner";

// Read-only detail viewer for any calendar event. For manual personal rides,
// it exposes "Appeler / WhatsApp / Bon de transport / Modifier". S.DRIVE rides
// are informational only (driver edits them from the main dashboard).
export default function CalendarEventDetail({ event, onClose, onEdit, readOnly = false }) {
  const [transportText, setTransportText] = useState(null);
  const [loadingTransport, setLoadingTransport] = useState(false);
  if (!event) return null;
  const color = EVENT_COLORS[event.event_type] || EVENT_COLORS.personal_ride;
  const Icon = event.event_type === "sdrive_ride" ? Path
             : event.event_type === "unavailability" ? Prohibit : Clock;

  const isPersonal = event.event_type === "personal_ride";
  const isSdrive = event.event_type === "sdrive_ride";
  const canEdit = !readOnly && event.source === "manual";

  const loadTransport = async () => {
    setLoadingTransport(true);
    try {
      const { data } = await api.get(`/calendar/events/${event.id}/transport-order`);
      setTransportText(data.text || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bon indisponible");
    } finally { setLoadingTransport(false); }
  };

  const phone = event.client_phone || "";

  return (
    <Dialog open={!!event} onOpenChange={onClose}>
      <DialogContent data-testid="calendar-event-detail" className="bg-[#121212] border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${color.text}`}>
            <Icon size={18} weight="bold" /> {color.label}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            {formatRange(event.start_datetime, event.end_datetime)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 text-sm">
          {isSdrive && (
            <>
              <Row label="Trajet" value={event.route_label || event.title} />
              {event.reference && <Row label="Référence" value={event.reference} />}
              {event.driver_fare != null && <Row label="Tarif" value={`${event.driver_fare} EUR`} />}
              <p className="text-xs text-zinc-500 pt-1">Gérez cette course depuis l'onglet "Mes courses".</p>
            </>
          )}
          {isPersonal && (
            <>
              {event.client_name && <Row label="Client" value={event.client_name} />}
              {event.client_phone && <Row label="Téléphone" value={event.client_phone} />}
              {event.pickup_location && <Row label="Départ" value={event.pickup_location} />}
              {event.dropoff_location && <Row label="Arrivée" value={event.dropoff_location} />}
              {event.notes && <Row label="Notes" value={event.notes} multiline />}
            </>
          )}
          {event.event_type === "unavailability" && event.notes && (
            <Row label="Raison" value={event.notes} multiline />
          )}
          {event.event_type === "unavailability" && !event.notes && (
            <p className="text-sm text-zinc-400">Plage bloquée — vous êtes indisponible pendant ce créneau.</p>
          )}
        </div>

        {isPersonal && phone && !readOnly && (
          <div className="flex gap-2">
            <Button data-testid="event-call" onClick={() => { window.location.href = `tel:${phone}`; }}
              variant="outline" className="flex-1 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 h-10">
              <Phone size={16} className="mr-1.5" /> Appeler
            </Button>
            <Button data-testid="event-whatsapp" onClick={() => openWhatsApp(phone, "")}
              variant="outline" className="flex-1 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 h-10">
              <WhatsappLogo size={16} className="mr-1.5" /> WhatsApp
            </Button>
          </div>
        )}

        {isPersonal && !readOnly && (
          <div className="flex gap-2">
            <Button data-testid="event-transport" onClick={loadTransport} disabled={loadingTransport}
              variant="outline" className="flex-1 border-sky-500/40 text-sky-400 hover:bg-sky-500/10 h-10">
              <FileText size={16} className="mr-1.5" /> {loadingTransport ? "…" : "Bon de transport"}
            </Button>
            {canEdit && (
              <Button data-testid="event-edit" onClick={() => onEdit?.(event)} variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-10 px-3">
                <PencilSimple size={16} />
              </Button>
            )}
          </div>
        )}

        {event.event_type === "unavailability" && canEdit && (
          <Button data-testid="event-edit" onClick={() => onEdit?.(event)} variant="outline"
            className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-10">
            <PencilSimple size={16} className="mr-1.5" /> Modifier
          </Button>
        )}

        {transportText && (
          <div className="mt-2 bg-[#1A1A1A] border border-zinc-800 rounded p-3 max-h-72 overflow-y-auto">
            <pre className="text-[11px] text-zinc-200 whitespace-pre-wrap font-mono" data-testid="transport-text">{transportText}</pre>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, multiline = false }) {
  return (
    <div className="flex gap-3 text-sm">
      <span className="w-24 shrink-0 text-zinc-500 uppercase text-xs tracking-wider pt-0.5">{label}</span>
      <span className={`flex-1 text-zinc-200 ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>{value}</span>
    </div>
  );
}
